import React, { useState } from 'react';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogOverlay,
  ScaleFade,
  useToast,
} from '@chakra-ui/react';
import * as authApi from '@/api/authentication';
import MobileInput from './Mobile';
import OtpInput from './Otp';
import { storage } from '@/utils/storage';
import { Cart } from '@shop/models/src/cart';
import * as cartApi from '@/api/cart';
import { useCartCtx } from '@/app/cart/context/cart';

export enum Steps {
  mobile = 'mobile',
  otp = 'otp',
}

export function Login({ onLogin = () => {} }: { onLogin?: VoidFunction }) {
  const toast = useToast();
  const [step, setStep] = useState(() => {
    const url = new URL(window.location.href);
    return url.searchParams.get('step') || Steps.mobile;
  });

  function changeStep(step: Steps) {
    setStep(step);
    const url = new URL(window.location.href);
    url.searchParams.set('step', step);
    window.history.replaceState({}, '', url);
  }

  async function sendOtp(mobileNumber: string) {
    try {
      await authApi.sendOtp({
        mobileNumber,
      });
      changeStep(Steps.otp);
    } catch (e: any) {
      if (e.status === 429) {
        toast({
          title: 'چند دقیقهٔ دیگر دوباره تلاش کنید.',
          status: 'error',
        });
      }
      throw e;
    }
  }

  return (
    <>
      <ScaleFade
        in={step == Steps.mobile}
        unmountOnExit
        style={{ position: 'absolute' }}
      >
        <MobileInput sendOtp={sendOtp} />
      </ScaleFade>

      <ScaleFade
        in={step === Steps.otp}
        unmountOnExit
        style={{ position: 'absolute' }}
      >
        <OtpInput
          onSuccess={async () => {
            await onLogin();
            changeStep(Steps.mobile);
            toast({
              title: 'شما با موفقیت وارد شدید!',
              status: 'success',
            });
          }}
          sendOtp={sendOtp}
          onResendClick={() => changeStep(Steps.mobile)}
        />
      </ScaleFade>
    </>
  );
}

type LoginModalProps = {
  isOpen: boolean;
  onLogin?: VoidFunction;
  onClose: VoidFunction;
};
export default function LoginModal({
  isOpen,
  onLogin,
  onClose,
}: LoginModalProps) {
  const cancelRef = React.useRef(null);
  const toast = useToast();
  const { setCart } = useCartCtx();

  async function syncCart() {
    try {
      const cache = storage.local.cart.get<Cart>();
      if (!cache.items.length) {
        const cart = (await cartApi.getCart()).body;
        setCart(cart);
      } else {
        let cart = (await cartApi.updateCart(cache.items[0]!)).body; // create the cart on first request if it doesn't exist, then add the rest of the items to avoid database concurrency errors
        if (cache.items.length > 1) {
          cart = (
            await Promise.all(
              cache.items.slice(1).map((i) => cartApi.updateCart(i)),
            )
          ).pop()!.body;
        }
        setCart(cart);
      }
    } catch (e) {
      toast({
        status: 'error',
        description:
          'خطایی در بروزرسانی سبدخرید رخ داد. لطفا مجدد تلاش نمایید.',
      });
      throw e;
    }
  }

  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={onClose}
      isCentered
    >
      <AlertDialogOverlay zIndex={'popover'}>
        <AlertDialogContent mx={4} w={'100%'}>
          <AlertDialogBody
            position={'relative'}
            display={'flex'}
            alignItems={'center'}
            justifyContent={'center'}
            p={0}
          >
            <Login
              onLogin={() => {
                syncCart();
                onLogin?.();
                onClose();
              }}
            />
          </AlertDialogBody>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
}
